/**
 * StatsEmbedWidget — Embedded data source browser for all sports
 * Shows tabbed iframe embeds for each data source within a sport
 * CSS prefix: sew-
 */

import { useState, useMemo } from 'react'
import { ExternalLink, Database, RefreshCw } from 'lucide-react'
import './StatsEmbedWidget.css'

// ── Data sources by sport ──
const SPORT_SOURCES = {
  'mlb-stats': {
    sport: 'MLB / College Baseball',
    sources: [
      { id: 'savant', label: 'Baseball Savant', url: 'https://baseballsavant.mlb.com', desc: 'Statcast data, spray charts, pitch tracking, expected stats' },
      { id: 'fangraphs', label: 'FanGraphs', url: 'https://www.fangraphs.com', desc: 'Advanced stats, projections, leaderboards, depth charts' },
      { id: 'bbref', label: 'Baseball Reference', url: 'https://www.baseball-reference.com', desc: 'Historical stats, game logs, player pages, WAR' },
      { id: 'umpire', label: 'Umpire Scorecard', url: 'https://umpscorecards.com', desc: 'Umpire accuracy metrics, strike zone analysis, game scorecards' },
      { id: 'bbprospectus', label: 'Baseball Prospectus', url: 'https://www.baseballprospectus.com', desc: 'DRC+, PECOTA projections, prospect rankings' },
      { id: 'brooks', label: 'Brooks Baseball', url: 'https://www.brooksbaseball.net', desc: 'PITCHf/x data, pitch movement plots, release points' },
      { id: 'tango', label: 'TangoTiger', url: 'https://www.tangotiger.com', desc: 'The Book blog, wOBA weights, custom research tools' },
      { id: 'mlbcom', label: 'MLB.com Stats', url: 'https://www.mlb.com/stats', desc: 'Official MLB statistics, standings, schedule' },
    ],
  },
  'nba-stats': {
    sport: 'NBA / College Basketball',
    sources: [
      { id: 'nbastats', label: 'NBA.com Stats', url: 'https://www.nba.com/stats', desc: 'Official NBA stats, player tracking, shot charts' },
      { id: 'bbref-nba', label: 'Basketball Reference', url: 'https://www.basketball-reference.com', desc: 'Historical stats, advanced metrics, game logs' },
      { id: 'ctg', label: 'Cleaning the Glass', url: 'https://cleaningtheglass.com', desc: 'Lineup data, on/off stats, shot frequency' },
      { id: 'kenpom', label: 'KenPom', url: 'https://kenpom.com', desc: 'College basketball efficiency ratings, tempo-free stats' },
      { id: 'barttorvik', label: 'Barttorvik', url: 'https://barttorvik.com', desc: 'College basketball rankings, player stats, game predictions' },
      { id: 'synergy', label: 'Synergy Sports', url: 'https://synergysports.com', desc: 'Play-type analytics, video-indexed stats' },
    ],
  },
  'nfl-stats': {
    sport: 'NFL / College Football',
    sources: [
      { id: 'pfref', label: 'Pro Football Reference', url: 'https://www.pro-football-reference.com', desc: 'NFL stats, game logs, draft history, AV' },
      { id: 'nflverse', label: 'nflverse (GitHub)', url: 'https://github.com/nflverse', desc: 'nflfastR, nflreadr — open-source NFL play-by-play data' },
      { id: 'espn-nfl', label: 'ESPN NFL', url: 'https://www.espn.com/nfl/stats', desc: 'ESPN NFL statistics, QBR, win probability' },
      { id: 'cfbdata', label: 'CFB Data', url: 'https://collegefootballdata.com', desc: 'College football API, play-by-play, recruiting, EPA' },
      { id: 'cfbref', label: 'Sports Reference CFB', url: 'https://www.sports-reference.com/cfb', desc: 'College football historical stats and records' },
    ],
  },
  'nhl-stats': {
    sport: 'NHL',
    sources: [
      { id: 'nhlcom', label: 'NHL.com Stats', url: 'https://www.nhl.com/stats', desc: 'Official NHL statistics, standings, schedule' },
      { id: 'hkref', label: 'Hockey Reference', url: 'https://www.hockey-reference.com', desc: 'Historical NHL stats, player pages, game logs' },
      { id: 'nst', label: 'Natural Stat Trick', url: 'https://www.naturalstattrick.com', desc: 'Shot attempt data, on-ice metrics, line combos' },
      { id: 'moneypuck', label: 'MoneyPuck', url: 'https://moneypuck.com', desc: 'Expected goals, game predictions, player cards' },
      { id: 'evolving', label: 'Evolving Hockey', url: 'https://evolving-hockey.com', desc: 'GAR/WAR models, contract projections, RAPM' },
    ],
  },
  'cfb-stats': {
    sport: 'College Football',
    sources: [
      { id: 'cfbdata', label: 'CFB Data API', url: 'https://collegefootballdata.com', desc: 'Open API for college football play-by-play, recruiting, and team stats' },
      { id: 'cfbref', label: 'Sports Reference CFB', url: 'https://www.sports-reference.com/cfb', desc: 'Historical college football statistics' },
      { id: 'espn-cfb', label: 'ESPN CFB', url: 'https://www.espn.com/college-football/stats', desc: 'ESPN college football stats and rankings' },
      { id: 'sp-cfb', label: 'SP+ Ratings', url: 'https://www.espn.com/college-football/insider/story/_/id/37274086', desc: 'Bill Connelly SP+ predictive ratings' },
    ],
  },
  'cbb-stats': {
    sport: 'College Basketball',
    sources: [
      { id: 'kenpom', label: 'KenPom', url: 'https://kenpom.com', desc: 'Efficiency ratings, tempo-free stats, game predictions' },
      { id: 'barttorvik', label: 'Barttorvik', url: 'https://barttorvik.com', desc: 'T-Rank ratings, player stats, game simulations' },
      { id: 'cbbref', label: 'Sports Reference CBB', url: 'https://www.sports-reference.com/cbb', desc: 'Historical college basketball statistics' },
      { id: 'espn-cbb', label: 'ESPN CBB', url: 'https://www.espn.com/mens-college-basketball/stats', desc: 'ESPN college basketball stats and BPI' },
      { id: 'warrennolan', label: 'Warren Nolan', url: 'https://www.warrennolan.com', desc: 'NET rankings, resume tracker, bracket projections' },
    ],
  },
  'soccer-stats': {
    sport: 'Soccer',
    sources: [
      { id: 'fbref', label: 'FBref', url: 'https://fbref.com', desc: 'Football Reference — xG, pass networks, shot maps, scouting reports' },
      { id: 'understat', label: 'Understat', url: 'https://understat.com', desc: 'Expected goals, shot data, player xG timelines' },
      { id: 'whoscored', label: 'WhoScored', url: 'https://www.whoscored.com', desc: 'Match ratings, heatmaps, chalkboards, statistical analysis' },
      { id: 'transfermarkt', label: 'Transfermarkt', url: 'https://www.transfermarkt.com', desc: 'Player values, transfer history, contract data' },
    ],
  },
}

export default function StatsEmbedWidget({ sportTab = 'mlb-stats' }) {
  const config = SPORT_SOURCES[sportTab] || SPORT_SOURCES['mlb-stats']
  const [activeSource, setActiveSource] = useState(config.sources[0]?.id)
  const [iframeKey, setIframeKey] = useState(0)

  const source = useMemo(() => config.sources.find(s => s.id === activeSource) || config.sources[0], [config, activeSource])

  return (
    <div className="widget sew-widget">
      <div className="widget-header">
        <Database size={16} />
        <h2 className="widget-title">{config.sport} Data Sources</h2>
        <span className="sew-count">{config.sources.length} sources</span>
      </div>

      {/* Source tabs */}
      <div className="sew-tabs">
        {config.sources.map(src => (
          <button
            key={src.id}
            className={`sew-tab ${activeSource === src.id ? 'active' : ''}`}
            onClick={() => setActiveSource(src.id)}
            title={src.desc}
          >
            {src.label}
          </button>
        ))}
      </div>

      {/* Source info bar */}
      <div className="sew-info-bar">
        <span className="sew-info-desc">{source.desc}</span>
        <div className="sew-info-actions">
          <button className="sew-refresh-btn" onClick={() => setIframeKey(k => k + 1)} title="Reload">
            <RefreshCw size={12} />
          </button>
          <a href={source.url} target="_blank" rel="noopener noreferrer" className="sew-open-btn">
            Open in new tab <ExternalLink size={12} />
          </a>
        </div>
      </div>

      {/* Embedded iframe */}
      <div className="sew-iframe-wrap">
        <iframe
          key={`${source.id}-${iframeKey}`}
          src={source.url}
          className="sew-iframe"
          title={source.label}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          loading="lazy"
        />
        <div className="sew-iframe-fallback">
          <p>If the content doesn't load, some sites block iframe embedding.</p>
          <a href={source.url} target="_blank" rel="noopener noreferrer" className="sew-fallback-link">
            Open {source.label} directly <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </div>
  )
}
